import { Target, Clapperboard, Megaphone, Globe, RefreshCw, Bot, ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Reveal, Overline } from "./Reveal";

const SERVICES = [
  { n: "01", icon: Target, title: "Strategy & Growth", desc: "Marketing strategy, campaign planning, offer positioning, customer acquisition strategy, and growth consulting." },
  { n: "02", icon: Clapperboard, title: "Content & Creative", desc: "Cinematic video, photography, social content, advertising creative, and brand storytelling designed to capture attention." },
  { n: "03", icon: Megaphone, title: "Paid Advertising", desc: "Strategic paid-media campaigns built around customer acquisition, lead generation, retargeting, and measurable outcomes." },
  { n: "04", icon: Globe, title: "Websites & Funnels", desc: "Conversion-focused websites, landing pages, lead-generation funnels, and booking systems built around customer action." },
  { n: "05", icon: RefreshCw, title: "CRM & Automation", desc: "Lead capture, automated follow-up, appointment workflows, pipeline management, and customer lifecycle automation." },
  { n: "06", icon: Bot, title: "AI Infrastructure", desc: "AI-powered systems that communicate, qualify opportunities, and automate repetitive work — assistants, booking, follow-up, support, voice.", chips: ["AI assistants", "Lead qualification", "AI booking", "SMS / email follow-up", "AI support", "AI voice"] },
];

export default function Services() {
  const navigate = useNavigate();
  return (
    <section id="services" data-testid="services-section" className="border-t border-line px-6 py-28 md:px-12 md:py-40">
      <div className="mx-auto max-w-[1600px]">
        <Reveal><Overline>02 — What We Build</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-8 max-w-4xl font-display text-3xl font-extrabold uppercase leading-[1.05] tracking-tight text-white md:text-5xl lg:text-6xl">
            Everything your business needs to turn <span className="text-zinc-500">attention into revenue</span>
          </h2>
        </Reveal>

        <div className="mt-20 grid grid-cols-1 gap-px bg-line md:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.06} className="h-full">
              <article
                data-testid={`service-card-${s.n}`}
                className="group flex h-full flex-col justify-between border border-transparent bg-obsidian p-10 transition-colors duration-500 hover:border-white/60"
              >
                <div>
                  <div className="flex items-start justify-between">
                    <s.icon size={28} strokeWidth={1.5} className="text-zinc-500 transition-colors duration-500 group-hover:text-crimson" />
                    <span className="font-mono text-xs text-zinc-600">{s.n}</span>
                  </div>
                  <h3 className="mt-10 font-display text-xl font-bold uppercase tracking-tight text-white transition-transform duration-500 group-hover:-translate-y-1 md:text-2xl">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-zinc-400">{s.desc}</p>
                  {s.chips && (
                    <div className="mt-6 flex flex-wrap gap-2">
                      {s.chips.map((c) => (
                        <span key={c} className="border border-line px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-zinc-500">{c}</span>
                      ))}
                    </div>
                  )}
                </div>
                <ArrowUpRight size={18} className="mt-10 text-zinc-700 transition-colors duration-500 group-hover:text-white" />
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <button
            data-testid="services-explore-btn"
            onClick={() => navigate("/book")}
            className="mt-16 border border-white/30 px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:border-white hover:bg-white hover:text-black"
          >
            Explore Solutions
          </button>
        </Reveal>
      </div>
    </section>
  );
}
